/**
 * @file Organism: OptionsModal
 * @description Modal overlay for game settings including audio controls and give up option
 */

import type React from 'react';
import { useAudioContext } from '../../contexts/AudioContext';
import { analytics } from '../../services/analyticsService';
import PhoneFrameBorder from '../atoms/PhoneFrameBorder';

const checkboxStyles = `
  input[type="checkbox"]:checked::after {
    content: "✓";
    position: absolute;
    color: #00ffff;
    font-size: 18px;
    font-weight: bold;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`;

interface OptionsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  onClose: () => void;
  /** Function to handle give up action */
  onGiveUp?: () => void;
  /** When true, hide the EXIT / give up button (e.g. standalone/demo link mode) */
  hideGiveUp?: boolean;
}

const OptionsModal: React.FC<OptionsModalProps> = ({
  isOpen,
  onClose,
  onGiveUp,
  hideGiveUp = false,
}) => {
  const {
    isMusicEnabled,
    isSfxEnabled,
    musicVolume,
    sfxVolume,
    toggleMusic,
    toggleSfx,
    setMusicVolume,
    setSfxVolume,
  } = useAudioContext();

  if (!isOpen) return null;

  return (
    <>
      <style>{checkboxStyles}</style>
      <div
        className="absolute inset-0 z-[9999] pointer-events-none"
        style={{ isolation: 'isolate' }}
      >
        {/* Background overlay with blur - negative inset overcomes sub-pixel/backdrop-filter edge rendering gaps */}
        <div className="absolute -inset-px bg-[rgba(20,11,12,0.9)] backdrop-blur-sm pointer-events-auto z-[1]" />

        <PhoneFrameBorder />

        <div className="relative w-full h-full shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] flex items-center justify-center px-6 py-0 z-[2] pointer-events-none">
          <div className="relative w-full h-[660px] flex flex-col z-[2] pointer-events-auto">
            <div className="pt-6 w-full px-6">
              <h1 className="text-[36px] uppercase text-white font-oswald font-normal tracking-[0.05em] text-center mb-4">
                OPTIONS
              </h1>
            </div>

            <div className="flex-1 px-6 space-y-4">
              <div className="w-full space-y-2">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      id="music-toggle"
                      checked={isMusicEnabled}
                      onChange={toggleMusic}
                      className="w-6 h-6 appearance-none bg-black border-2 border-white rounded-sm relative cursor-pointer"
                      style={{
                        accentColor: '#00ffff',
                      }}
                    />
                    <label
                      htmlFor="music-toggle"
                      className="text-white font-oswald text-lg uppercase tracking-[0.05em] font-normal"
                    >
                      MUSIC
                    </label>
                  </div>
                </div>
                <div className="w-full">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={musicVolume}
                    onChange={(e) => setMusicVolume(parseFloat(e.target.value))}
                    disabled={!isMusicEnabled}
                    className="w-full h-2 bg-gray-500 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #00ffff 0%, #00ffff ${musicVolume * 100}%, rgba(255,255,255,0.5) ${musicVolume * 100}%, rgba(255,255,255,0.5) 100%)`,
                    }}
                  />
                </div>
              </div>

              <div className="w-full space-y-2">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      id="sfx-toggle"
                      checked={isSfxEnabled}
                      onChange={toggleSfx}
                      className="w-6 h-6 appearance-none bg-black border-2 border-white rounded-sm relative cursor-pointer"
                      style={{
                        accentColor: '#00ffff',
                      }}
                    />
                    <label
                      htmlFor="sfx-toggle"
                      className="text-white font-oswald text-lg uppercase tracking-[0.05em] font-normal"
                    >
                      SOUND EFFECTS
                    </label>
                  </div>
                </div>
                <div className="w-full">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={sfxVolume}
                    onChange={(e) => setSfxVolume(parseFloat(e.target.value))}
                    disabled={!isSfxEnabled}
                    className="w-full h-2 bg-gray-500 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #00ffff 0%, #00ffff ${sfxVolume * 100}%, rgba(255,255,255,0.5) ${sfxVolume * 100}%, rgba(255,255,255,0.5) 100%)`,
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="w-full px-6 pb-3 space-y-2">
              <button
                type="button"
                onClick={onClose}
                className="w-full py-2 bg-white text-black font-oswald text-xl uppercase tracking-wider font-bold hover:bg-gray-100 transition-colors"
                style={{ borderRadius: '2px' }}
              >
                CLOSE
              </button>

              {onGiveUp && !hideGiveUp && (
                <button
                  type="button"
                  onClick={() => {
                    analytics.trackButtonClick('Exit', 'options-modal-give-up');
                    // Note: Session does NOT end here - user gives up current level but
                    // can still view results and navigate to other cases
                    onGiveUp();
                  }}
                  className="w-full py-2 bg-black bg-opacity-50 text-white font-oswald text-xl uppercase tracking-wider font-bold hover:bg-opacity-70 transition-colors"
                  style={{
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '2px',
                  }}
                >
                  EXIT
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OptionsModal;
